import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import PlayerDetailPanel from '../components/players/PlayerDetailPanel';
import { dispatchShopCreditBalanceRefresh } from '../constants';
import { fetchDashboard, fetchShopPlayer } from '../services/api';
import type { ShopPlayer } from '../types';

function parseAutoAction(raw: string | null): 'deposit' | 'redeem' | null {
  if (raw === 'deposit' || raw === 'add') return 'deposit';
  if (raw === 'redeem' || raw === 'cashout') return 'redeem';
  return null;
}

export default function PlayerDetailPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const [searchParams] = useSearchParams();
  const [autoAction] = useState(() => parseAutoAction(searchParams.get('action')));
  const [player, setPlayer] = useState<ShopPlayer | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shopCreditBalance, setShopCreditBalance] = useState<number | null>(null);
  const [shopCreditBalanceLoading, setShopCreditBalanceLoading] = useState(true);

  const loadShopCreditBalance = useCallback(async () => {
    setShopCreditBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data != null) {
        const raw = res.data.credit_balance;
        const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
        setShopCreditBalance(Number.isFinite(n) ? n : null);
      } else {
        setShopCreditBalance(null);
      }
    } catch {
      setShopCreditBalance(null);
    } finally {
      setShopCreditBalanceLoading(false);
    }
  }, []);

  const loadPlayer = useCallback(async () => {
    if (!playerId) {
      setNotFound(true);
      setPlayer(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const res = await fetchShopPlayer(playerId);
      if (res.success && res.data) {
        setPlayer(res.data);
        setNotFound(false);
      } else {
        setPlayer(null);
        setNotFound(true);
      }
    } catch {
      setPlayer(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    void loadShopCreditBalance();
  }, [loadShopCreditBalance]);

  useEffect(() => {
    void loadPlayer();
  }, [loadPlayer]);

  const onChanged = useCallback(() => {
    void loadPlayer();
    void loadShopCreditBalance();
    dispatchShopCreditBalanceRefresh();
  }, [loadPlayer, loadShopCreditBalance]);

  return (
    <AppLayout contentFullWidth>
      <Link
        to="/players"
        className="mb-4 inline-flex items-center gap-0.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        Back to Players
      </Link>

      {loading ? (
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      ) : notFound || !player ? (
        <div className="rounded-xl border border-emerald-100 bg-white shadow-sm">
          <PortalEmptyState
            icon="👤"
            title="Player not found"
            description="This Player may have been removed or is not assigned to your Shop."
            primaryAction={{ kind: 'link', to: '/players', label: 'Back to Player list' }}
          />
        </div>
      ) : (
        <PlayerDetailPanel
          player={player}
          shopWallet={{ balance: shopCreditBalance, loading: shopCreditBalanceLoading }}
          onChanged={onChanged}
          autoAction={autoAction}
        />
      )}
    </AppLayout>
  );
}
